import React from 'react';
import {
  LayoutDashboard, TrendingUp, Shield, BarChart3,
  Droplets, Compass, FlaskConical, GraduationCap, Lock, Activity, History, BellRing, BarChart2, Brain, UserCircle, UserPlus, Users, ClipboardList, Settings, MessageCircle, MessagesSquare, CreditCard, HeartHandshake, Megaphone, Eye, CalendarClock, FileText, LineChart, HardDriveDownload
} from 'lucide-react';

const tabs = [
  { key: 'dashboard', label: 'Mission Control', icon: LayoutDashboard, drop: 1, available: true },
  { key: 'yieldCurve', label: 'Yield Curve', icon: TrendingUp, drop: 2, available: true },
  { key: 'vigilantes', label: 'Vigilantes', icon: Shield, drop: 3, available: true },
  { key: 'credit', label: 'Fear Gauge', icon: BarChart3, drop: 4, available: true },
  { key: 'flows', label: 'Money Flows', icon: Droplets, drop: 5, available: true },
  { key: 'strategy', label: 'Strategist', icon: Compass, drop: 6, available: true },
  { key: 'simulator', label: 'Simulator', icon: FlaskConical, drop: 7, available: true },
  { key: 'learn', label: 'Learn', icon: GraduationCap, drop: 7, available: true },
  // Phase 29: keep Regime Alerts visible as a first-class tab instead of
  // letting it disappear at the far right on narrower screens.
  { key: 'alerts', label: 'Regime Alerts', icon: BellRing, drop: 27, available: true, priority: true },
  { key: 'replay', label: 'Replay', icon: History, drop: 26, available: true },
  { key: 'backtesting', label: 'Backtesting', icon: BarChart2, drop: 39, available: true },
  { key: 'probability', label: 'Probability', icon: Brain, drop: 40, available: true },
  { key: 'adminHealth', label: 'Admin Health', icon: Activity, drop: 14, available: true, adminOnly: true },
  { key: 'invites', label: 'Invites', icon: UserPlus, drop: 43, available: true, adminOnly: true },
  { key: 'directory', label: 'Members', icon: Users, drop: 43, available: true, adminOnly: true },
  { key: 'billing', label: 'Billing', icon: CreditCard, drop: 43, available: true, adminOnly: true },
  { key: 'charity', label: 'Charity', icon: HeartHandshake, drop: 43, available: true, adminOnly: true },
  { key: 'banners', label: 'Banners', icon: Megaphone, drop: 43, available: true, adminOnly: true },
  { key: 'menuAccess', label: 'Menu Access', icon: Eye, drop: 43, available: true, adminOnly: true },
  { key: 'macroCalendar', label: 'Macro Calendar', icon: CalendarClock, drop: 44, available: true, adminOnly: true },
  { key: 'secResearch', label: 'SEC Filings', icon: FileText, drop: 45, available: true, adminOnly: true },
  { key: 'waitlist', label: 'Waitlist', icon: ClipboardList, drop: 43, available: true, adminOnly: true },
  { key: 'siteMode', label: 'Site Mode', icon: Settings, drop: 43, available: true, adminOnly: true },
  { key: 'feedback', label: 'Feedback', icon: MessageCircle, drop: 43, available: true, adminOnly: true },
  { key: 'messages', label: 'Messages', icon: MessagesSquare, drop: 43, available: true, adminOnly: true },
  { key: 'analytics', label: 'Analytics', icon: LineChart, drop: 43, available: true, adminOnly: true },
  { key: 'backups', label: 'Backups', icon: HardDriveDownload, drop: 43, available: true, adminOnly: true },
  { key: 'account', label: 'Account', icon: UserCircle, drop: 43, available: true },
];

export default function Navigation({ activeTab = 'dashboard', onTabChange, user, hiddenTabs = [], auctionAlert = null }) {
  return (
    <nav className="border-b border-mentor-border/30 bg-mentor-bg/80 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-1 py-2 overflow-x-auto xl:overflow-visible xl:flex-wrap scrollbar-hide">
          {tabs.map((tab) => {
            if (tab.adminOnly && !user?.permissions?.canAccessAdmin) return null;
            // Role-based visibility (SuperAdmin is never restricted — server
            // returns an empty hidden list for them).
            if (hiddenTabs.includes(tab.key)) return null;
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                onClick={() => tab.available && onTabChange?.(tab.key)}
                className={`nav-tab flex items-center gap-2 whitespace-nowrap relative shrink-0 ${
                  isActive ? 'active' : ''
                } ${!tab.available ? 'opacity-40 cursor-not-allowed' : ''}`}
                disabled={!tab.available}
              >
                <Icon size={15} />
                <span>{tab.label}</span>
                {tab.key === 'vigilantes' && auctionAlert?.active && (
                  <span
                    title={auctionAlert.tone === 'strong' ? 'Strong demand at the latest Treasury auction' : 'Weak demand at the latest Treasury auction'}
                    className={`inline-block h-2 w-2 rounded-full ${auctionAlert.tone === 'strong' ? 'bg-green-400' : 'bg-red-400'} animate-pulse`}
                  />
                )}
                {tab.priority && (
                  <span className="rounded-full border border-yellow-400/30 bg-yellow-400/10 px-1.5 py-0.5 text-[9px] text-yellow-100 font-display font-bold uppercase tracking-wider">
                    Watch
                  </span>
                )}
                {!tab.available && (
                  <span className="flex items-center gap-0.5 text-[10px] text-mentor-muted">
                    <Lock size={9} />
                    D{tab.drop}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
